import { useEffect, useRef, useState } from "react";
import { Share2, Download, Copy, CheckCheck, X, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

const TEAL = "#0D9488";
const TEAL_DARK = "#0F766E";

/** Renders the user's weight change + streak into a shareable PNG card.
 *  Uses HTML5 Canvas so no server involvement — the client can screenshot,
 *  download, or use Web Share API to post it anywhere.
 */
export default function TransformationCardDialog({ open, onClose }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [pngUrl, setPngUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Pull weights + user for a minimal card
    Promise.all([
      api.get("/weights?limit=180").catch(() => ({ data: [] })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]).then(([w, u]) => {
      const weights = Array.isArray(w.data) ? w.data : [];
      const sorted = [...weights].sort((a, b) => new Date(a.logged_at) - new Date(b.logged_at));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const change = first && last ? +(last.weight - first.weight).toFixed(1) : 0;
      const days = first && last ? Math.max(1, Math.round((new Date(last.logged_at) - new Date(first.logged_at)) / 86400000)) : 0;
      setStats({
        user: u.data || {},
        weights: sorted,
        first_weight: first?.weight,
        last_weight: last?.weight,
        change,
        days,
      });
    }).finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!stats || !canvasRef.current) return;
    drawCard(canvasRef.current, stats).then((url) => setPngUrl(url));
  }, [stats]);

  const handleDownload = () => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = `amino-transformation-${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
    toast.success("Card downloaded");
  };

  const handleShare = async () => {
    if (!pngUrl) return;
    try {
      const blob = await (await fetch(pngUrl)).blob();
      const file = new File([blob], "transformation.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Amino Exchange transformation" });
      } else {
        handleDownload();
      }
    } catch {
      handleDownload();
    }
  };

  const handleCopy = async () => {
    if (!pngUrl) return;
    try {
      const blob = await (await fetch(pngUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Card copied to clipboard");
    } catch {
      toast.error("Copy isn't supported here — use Download instead.");
    }
  };

  if (!open) return null;

  const change = stats?.change ?? 0;
  const isLoss = change < 0;

  return createPortal(
    <div className="fixed inset-0 z-[9990] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="transformation-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="transformation-dialog">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0D9488]">Share your transformation</div>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">One tap. Ready to post.</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"
                  data-testid="transformation-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="h-72 bg-slate-100 rounded-lg animate-pulse" />
          ) : !stats || (stats.weights || []).length < 2 ? (
            <div className="text-sm text-slate-600 py-8 text-center" data-testid="transformation-empty">
              Log at least two weigh-ins to build a transformation card.
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
                <canvas ref={canvasRef} width={1080} height={1350} className="w-full h-auto block" data-testid="transformation-canvas" />
              </div>
              <div className="mt-3 text-[11px] text-slate-400 text-center">1080×1350 · Instagram-story friendly</div>
            </>
          )}
        </div>

        {stats?.weights?.length >= 2 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={handleCopy}
                    className="text-slate-700 hover:bg-slate-100" data-testid="transformation-copy">
              {copied ? <><CheckCheck className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={handleDownload}
                    className="text-slate-700 hover:bg-slate-100" data-testid="transformation-download">
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
            <Button type="button" size="sm" onClick={handleShare}
                    className="text-white" style={{ background: TEAL }} data-testid="transformation-share">
              <Share2 className="w-3.5 h-3.5 mr-1" /> Share
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

async function drawCard(canvas, stats) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;

  // Background
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#F0FDFA");
  grad.addColorStop(1, "#CCFBF1");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Header
  ctx.fillStyle = TEAL_DARK;
  ctx.font = "600 24px Inter, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("THE AMINO EXCHANGE", 60, 90);

  // User handle
  const handle = stats.user?.handle || (stats.user?.email || "you").split("@")[0];
  ctx.fillStyle = "#0F172A";
  ctx.font = "800 60px Inter, sans-serif";
  ctx.fillText(`@${handle}`, 60, 180);

  // Days label
  ctx.fillStyle = "#475569";
  ctx.font = "500 26px Inter, sans-serif";
  ctx.fillText(`${stats.days}-day transformation`, 60, 230);

  // Big weight change
  const change = stats.change;
  const isLoss = change < 0;
  ctx.fillStyle = isLoss ? TEAL : "#EA580C";
  ctx.font = "900 160px Inter, sans-serif";
  const sign = change > 0 ? "+" : "";
  const label = `${sign}${change.toFixed(1)} lb`;
  ctx.fillText(label, 60, 440);

  ctx.fillStyle = "#334155";
  ctx.font = "500 32px Inter, sans-serif";
  ctx.fillText(isLoss ? "Weight lost" : "Weight gained", 60, 490);

  // Sparkline
  const weights = stats.weights;
  const xs = weights.map((_, i) => i);
  const ys = weights.map((w) => w.weight);
  const minY = Math.min(...ys) - 1;
  const maxY = Math.max(...ys) + 1;
  const graphX = 60, graphY = 620, graphW = W - 120, graphH = 380;

  // Card behind sparkline
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, graphX - 20, graphY - 40, graphW + 40, graphH + 80, 24);
  ctx.fill();

  // Sparkline path
  ctx.strokeStyle = TEAL;
  ctx.lineWidth = 8;
  ctx.beginPath();
  weights.forEach((w, i) => {
    const x = graphX + (xs[i] / Math.max(1, xs.length - 1)) * graphW;
    const y = graphY + graphH - ((w.weight - minY) / (maxY - minY)) * graphH;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Endpoint dots
  const startPt = { x: graphX, y: graphY + graphH - ((weights[0].weight - minY) / (maxY - minY)) * graphH };
  const endPt = { x: graphX + graphW, y: graphY + graphH - ((weights[weights.length - 1].weight - minY) / (maxY - minY)) * graphH };
  ctx.fillStyle = TEAL_DARK;
  ctx.beginPath(); ctx.arc(startPt.x, startPt.y, 14, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(endPt.x, endPt.y, 18, 0, Math.PI * 2); ctx.fill();

  // Endpoint labels
  ctx.fillStyle = "#0F172A";
  ctx.font = "700 28px Inter, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(`${stats.first_weight} lb`, startPt.x + 20, startPt.y - 20);
  ctx.textAlign = "right";
  ctx.fillText(`${stats.last_weight} lb`, endPt.x - 20, endPt.y - 20);

  // Footer bar
  ctx.textAlign = "left";
  ctx.fillStyle = TEAL_DARK;
  ctx.font = "600 20px Inter, sans-serif";
  ctx.fillText("Track shots · meals · weight · supplements", 60, H - 100);
  ctx.fillStyle = "#0F172A";
  ctx.font = "800 32px Inter, sans-serif";
  ctx.fillText("aminoexchange.app", 60, H - 60);

  return canvas.toDataURL("image/png");
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
